/**
 * Notification rationale modal — shown once before the OS permission prompt.
 */

import React, { useId, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Image } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_FAMILY, FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';
import { useBackHandler } from '../../navigation/useBackHandler';

interface Props {
  visible: boolean;
  onAllow: () => void;
  onLater: () => void;
}

export default function NotificationRationale({ visible, onAllow, onLater }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const modalId = useId();

  useBackHandler({
    id: `notification-rationale-${modalId}`,
    label: 'Notification prompt',
    priority: 950,
    enabled: visible,
    onBack: () => {
      onLater();
      return true;
    },
  });

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onLater}>
      <View style={s.overlay}>
        <View style={s.card}>
          <View style={s.iconWrap}>
            <Ionicons name="notifications" size={40} color={colors.primary} />
          </View>
          <Text style={s.title}>{t('onboarding.notifRationaleTitle')}</Text>
          <Text style={s.body}>{t('onboarding.notifRationaleBody')}</Text>

          {/* Visual notification preview */}
          <View style={s.notifPreview}>
            <View style={s.notifHeader}>
              <Ionicons name="fish" size={16} color={colors.primary} />
              <Text style={s.notifAppName}>FishMemory</Text>
              <Text style={s.notifTime}>now</Text>
            </View>
            <Text style={s.notifTitle}>{t('onboarding.notifPreviewTitle', { defaultValue: 'Reminder' })}</Text>
            <Text style={s.notifBody}>{t('onboarding.notifPreviewBody', { defaultValue: "Don't forget: Buy groceries 🐟" })}</Text>
          </View>

          <TouchableOpacity style={s.allowBtn} onPress={onAllow} activeOpacity={0.85} accessibilityRole="button">
            <Text style={s.allowText}>{t('onboarding.notifRationaleAllow')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.laterBtn} onPress={onLater} accessibilityRole="button">
            <Text style={s.laterText}>{t('onboarding.notifRationaleLater')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: SPACING.xl,
    },
    card: {
      backgroundColor: colors.card,
      borderRadius: RADIUS.xl,
      padding: SPACING['2xl'],
      alignItems: 'center',
      width: '100%',
      maxWidth: 340,
    },
    iconWrap: {
      width: 72,
      height: 72,
      borderRadius: 36,
      backgroundColor: colors.primary + '14',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: SPACING.base,
    },
    title: {
      fontFamily: FONT_FAMILY.heading,
      fontSize: FONT_SIZE.lg,
      color: colors.text,
      marginBottom: SPACING.sm,
      textAlign: 'center',
    },
    body: {
      fontFamily: FONT_FAMILY.body,
      fontSize: FONT_SIZE.base,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: FONT_SIZE.base * 1.5,
      marginBottom: SPACING.md,
    },
    notifPreview: {
      width: '100%',
      backgroundColor: colors.background,
      borderRadius: RADIUS.lg,
      padding: SPACING.md,
      marginBottom: SPACING.xl,
      borderWidth: 1,
      borderColor: colors.border,
    },
    notifHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: SPACING.xs,
    },
    notifAppName: {
      fontFamily: FONT_FAMILY.bodySemibold,
      fontSize: FONT_SIZE.xs,
      color: colors.textSecondary,
      marginLeft: SPACING.xs,
      flex: 1,
    },
    notifTime: {
      fontFamily: FONT_FAMILY.body,
      fontSize: FONT_SIZE.xs,
      color: colors.textSecondary,
    },
    notifTitle: {
      fontFamily: FONT_FAMILY.bodySemibold,
      fontSize: FONT_SIZE.sm,
      color: colors.text,
      marginBottom: 2,
    },
    notifBody: {
      fontFamily: FONT_FAMILY.body,
      fontSize: FONT_SIZE.sm,
      color: colors.textSecondary,
    },
    allowBtn: {
      backgroundColor: colors.primary,
      paddingVertical: SPACING.md,
      paddingHorizontal: SPACING['2xl'],
      borderRadius: RADIUS.lg,
      width: '100%',
      alignItems: 'center',
      marginBottom: SPACING.md,
    },
    allowText: {
      fontFamily: FONT_FAMILY.bodySemibold,
      fontSize: FONT_SIZE.base,
      color: '#FFFFFF',
    },
    laterBtn: {
      paddingVertical: SPACING.sm,
    },
    laterText: {
      fontFamily: FONT_FAMILY.bodyMedium,
      fontSize: FONT_SIZE.sm,
      color: colors.textSecondary,
    },
  });
}
